import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import styles from './SupplierDashboard.module.css';

const STATUS_BADGE = {
  'Pending':    styles.badgePending,
  'On its way': styles.badgeOnWay,
  'Delivered':  styles.badgeDelivered,
  'Cancelled':  styles.badgeCancelled,
};

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function SupplierDashboard() {
  const { user }           = useAuth();
  const [products, setProducts] = useState([]);
  const [orders,   setOrders]   = useState([]);

  useEffect(() => {
    const unsubProducts = onSnapshot(collection(db, 'products'), snap => {
      setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'), limit(5));
    const unsubOrders = onSnapshot(q, snap => {
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsubProducts(); unsubOrders(); };
  }, []);

  const active    = products.filter(p => !p.isArchived);
  const lowStock  = active.filter(p => p.stock >= 10 && p.stock < 30);
  const critical  = active.filter(p => p.stock < 10);
  const pending   = orders.filter(o => o.status === 'Pending');

  const stats = [
    { label: 'Total Products',  value: active.length,   sub: 'Active items'     },
    { label: 'Low Stock',       value: lowStock.length,  sub: '10–29 units'      },
    { label: 'Critical Stock',  value: critical.length,  sub: 'Under 10 units'   },
    { label: 'Pending Orders',  value: pending.length,   sub: 'Awaiting dispatch' },
  ];

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={`Welcome back, ${user?.name}. Here's your supply overview.`}
      />

      <div className={styles.statsGrid}>
        {stats.map((s, i) => (
          <div
            key={i}
            className={`${styles.statCard} ${i === 2 && critical.length > 0 ? styles.statCardAlert : ''}`}
          >
            <p className={styles.statLabel}>{s.label}</p>
            <p className={styles.statValue}>{s.value}</p>
            <p className={styles.statSub}>{s.sub}</p>
          </div>
        ))}
      </div>

      <div className={styles.grid}>
        {/* Recent Orders */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>Recent Branch Orders</h3>
            <span className={styles.cardMeta}>{orders.length > 0 ? `Last ${orders.length} orders` : 'No orders yet'}</span>
          </div>
          {orders.length === 0 ? (
            <p className={styles.empty}>No orders have been placed yet.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Branch</th>
                  <th>Details</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id}>
                    <td className={styles.tdBranch}>{order.branch}</td>
                    <td className={styles.tdDetails}>{order.details}</td>
                    <td className={styles.tdTotal}>₱{order.total?.toLocaleString()}</td>
                    <td>
                      <span className={`${styles.badge} ${STATUS_BADGE[order.status] || ''}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className={styles.tdDate}>{formatDate(order.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Stock Alerts */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>Stock Alerts</h3>
            <span className={styles.cardMeta}>{critical.length + lowStock.length} item(s) need attention</span>
          </div>
          {critical.length === 0 && lowStock.length === 0 ? (
            <p className={styles.empty}>All products are well-stocked.</p>
          ) : (
            <ul className={styles.alertList}>
              {critical.map(p => (
                <li key={p.id} className={`${styles.alertItem} ${styles.alertCritical}`}>
                  <div className={styles.alertInfo}>
                    <span className={styles.alertName}>{p.name}</span>
                    <span className={styles.alertId}>{p.id}</span>
                  </div>
                  <div className={styles.alertRight}>
                    <span className={styles.alertStock}>{p.stock} units</span>
                    <span className={`${styles.badge} ${styles.badgeCritical}`}>Critical</span>
                  </div>
                </li>
              ))}
              {lowStock.map(p => (
                <li key={p.id} className={`${styles.alertItem} ${styles.alertLow}`}>
                  <div className={styles.alertInfo}>
                    <span className={styles.alertName}>{p.name}</span>
                    <span className={styles.alertId}>{p.id}</span>
                  </div>
                  <div className={styles.alertRight}>
                    <span className={styles.alertStock}>{p.stock} units</span>
                    <span className={`${styles.badge} ${styles.badgeLow}`}>Low Stock</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
